import { Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  exclusiveEndToLastDay,
  formatCalendarDate,
  instantToDate,
} from "../../utils/wage-dates";
import { getAssigneeName } from "../../utils/assignee-name";

export type AssignmentRow = {
  assignment: {
    id: string;
    effectiveStartsAt: string | Date;
    effectiveEndsAt: string | Date | null;
  };
  providerFirstName: string | null;
  providerLastName: string | null;
  providerEmail: string | null;
  poolName: string | null;
};

type AssignmentsListProps = {
  assignments: AssignmentRow[];
  timezoneId?: string;
  canManage: boolean;
  onEnd: (row: AssignmentRow) => void;
};

function describeWindow({
  row,
  timezoneId,
}: {
  row: AssignmentRow;
  timezoneId?: string;
}): string {
  const from = formatCalendarDate(
    instantToDate({ instant: row.assignment.effectiveStartsAt, timezoneId }),
  );
  if (!row.assignment.effectiveEndsAt) {
    return `From ${from}`;
  }
  const through = formatCalendarDate(
    exclusiveEndToLastDay({
      instant: row.assignment.effectiveEndsAt,
      timezoneId,
    }),
  );
  return `From ${from} through ${through}`;
}

export function AssignmentsList({
  assignments,
  timezoneId,
  canManage,
  onEnd,
}: AssignmentsListProps) {
  return (
    <Card className="rounded-2xl border border-slate-200/80 bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">Assignments</CardTitle>
      </CardHeader>
      <CardContent>
        {assignments.length === 0 && (
          <p className="text-sm text-slate-600">
            Not assigned to any provider or pool yet.
          </p>
        )}
        <ul className="divide-y divide-slate-100">
          {assignments.map((row) => (
            <li
              key={row.assignment.id}
              className="flex flex-wrap items-center justify-between gap-2 py-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 truncate font-medium text-slate-900">
                  {row.poolName && (
                    <Users
                      aria-label="Provider pool"
                      className="h-4 w-4 shrink-0 text-slate-500"
                    />
                  )}
                  {getAssigneeName(row)}
                </p>
                <p className="text-sm text-slate-600">
                  {describeWindow({ row, timezoneId })}
                </p>
              </div>
              {canManage && !row.assignment.effectiveEndsAt && (
                <Button variant="outline" size="sm" onClick={() => onEnd(row)}>
                  End
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
